package pl.michal_cyran.brand_hub.subpages.main.services

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ListStyleType
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.Width
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.listStyle
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.compose.ui.modifiers.transform
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowRight
import com.varabyte.kobweb.silk.components.icons.fa.FaCheck
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.AnimationTimingFunction.Companion.EaseIn
import org.jetbrains.compose.web.css.AnimationTimingFunction.Companion.EaseOut
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import pl.michal_cyran.brand_hub.components.widgets.VisibilityAware
import pl.michal_cyran.brand_hub.components.widgets.revealFadeSlideUpScale
import pl.michal_cyran.brand_hub.components.widgets.revealScaleIn
import pl.michal_cyran.brand_hub.components.widgets.staggerDelay
import pl.michal_cyran.brand_hub.style.CyanColors

@Composable
fun ServiceCard(item: ServiceItem, number: Int, c: CyanColors) {
    var hovered by remember { mutableStateOf(false) }

    // Stagger: card N animates a bit after card N-1
    val baseDelay = number * 150 // ms, same cadence as PropertyCard

    VisibilityAware(
        modifier = Modifier
            .id(item.id)
            .fillMaxWidth()
            .padding(32.px)
            .borderRadius(20.px)
            .backgroundColor(if (hovered) c.cardBgHover else c.cardBg)
            .border(1.px, LineStyle.Solid, if (hovered) c.borderHover else c.borderDefault)
            .boxShadow(
                offsetY = 8.px,
                blurRadius = 24.px,
                spreadRadius = (-4).px,
                color = if (hovered) c.shadowGlowMd else c.shadowGlowSm
            )
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
    ) { isVisible ->
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .opacity(if (isVisible) 1 else 0)
                .transform {
                    translateY(if (isVisible) 0.px else 24.px)
                    scale(if (isVisible) 1.0 else 0.96)
                }
                .revealFadeSlideUpScale(isVisible, delayMs = staggerDelay(number, step = 150))
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    Modifier
                        .size(56.px)
                        .borderRadius(16.px)
                        .backgroundColor(if (hovered) c.iconBoxBgHover else c.iconBoxBg)
                        .display(DisplayStyle.Flex)
                        .alignItems(AlignItems.Center)
                        .justifyContent(JustifyContent.Center)
                        .color(if (hovered) c.iconBoxColorHover else c.iconBoxColor)
                        .fontSize(22.px)
                        .revealScaleIn(isVisible, delayMs = staggerDelay(number, 150, offset = 150))
                ) { item.icon() }

                P(
                    Modifier
                        .fontSize(13.px)
                        .fontWeight(FontWeight.Medium)
                        .color(c.textDisabled)
                        .toAttrs()
                ) { Text("0${number}") }
            }

            H3(
                attrs = Modifier
                    .margin(top = 24.px)
                    .fontSize(21.px)
                    .fontWeight(700)
                    .color(if (hovered) c.cardTextHover else c.cardTextHeading)
                    .toAttrs()
            ) { Text(item.title) }

            P(
                attrs = Modifier
                    .margin(top = 12.px)
                    .color(c.cardTextBody)
                    .lineHeight(1.6)
                    .minHeight(80.px)
                    .toAttrs()
            ) { Text(item.description) }

            Link(path = item.link, modifier = Modifier.textDecorationLine(TextDecorationLine.None)) {
                Row(
                    Modifier
                        .display(DisplayStyle.Flex)
                        .alignItems(AlignItems.Center)
                        .gap(8.px)
                        .margin(top = 24.px, bottom = 24.px)
                        .padding(topBottom = 10.px, leftRight = 18.px)
                        .borderRadius(999.px)
                        .backgroundColor(if (hovered) c.primaryHover else c.primary)
                        .color(c.textOnPrimary)
                        .fontSize(14.px)
                        .fontWeight(FontWeight.Medium)
                        .width(Width.FitContent)
                ) {
                    Text("Dowiedz się więcej")
                    FaArrowRight(Modifier.fontSize(12.px))
                }
            }

            HorizontalDivider(
                Modifier.margin(bottom = 20.px).backgroundColor(c.divider)
            )

            P(
                attrs = Modifier
                    .fontSize(13.px)
                    .fontWeight(FontWeight.Medium)
                    .textTransform(TextTransform.Uppercase)
                    .letterSpacing(0.5.px)
                    .color(c.textMuted)
                    .toAttrs()
            ) { Text("W ofercie:") }

            Ul(
                attrs = Modifier
                    .margin(top = 16.px)
                    .display(DisplayStyle.Flex)
                    .flexDirection(FlexDirection.Column)
                    .gap(12.px)
                    .listStyle(ListStyleType.None)
                    .padding(0.px)
                    .toAttrs()
            ) {
                item.features.forEachIndexed { featureIndex, feature ->
                    Li(
                        attrs = Modifier
                            .display(DisplayStyle.Flex)
                            .alignItems(AlignItems.Center)
                            .gap(12.px)
                            .fontSize(14.px)
                            .color(c.cardTextBody)
                            .opacity(if (isVisible) 1 else 0)
                            .transform { translateX(if (isVisible) 0.px else (-10).px) }
                            .transition(
                                Transition.of(
                                    "opacity", 0.3.s, EaseOut,
                                    (baseDelay + 250 + featureIndex * 60).ms
                                ),
                                Transition.of(
                                    "transform", 0.3.s, EaseOut,
                                    (baseDelay + 250 + featureIndex * 60).ms
                                )
                            )
                            .toAttrs()
                    ) {
                        Box(
                            Modifier
                                .size(20.px)
                                .borderRadius(50.percent)
                                .backgroundColor(c.iconBoxBg)
                                .color(c.iconBoxColor)
                                .display(DisplayStyle.Flex)
                                .alignItems(AlignItems.Center)
                                .justifyContent(JustifyContent.Center)
                                .fontSize(10.px)
                        ) { FaCheck() }
                        Text(feature)
                    }
                }
            }
        }
    }
}